import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import TopBar from '../../components/TopBar';
import { verifyUserToken } from '../../api/auth';
import LoadingCard from '../../components/LoadingCard';
import LocalStorageHelper from '../../helpers/localStorageHelpers';
import { getRedirectRoute } from '../../helpers/bizDataHelpers';
import AnalyticsService from '../../services/analytics';
import Vent from '../../vent';

const EXCLUDE_ROUTES = [
  '/logout',
  '/invite/accept',
  '/restrict-login',
  '/login-restricted',
  '/forgot-password/verify',
  '/onboarding/biz-verify',
  '/login-trouble',
  '/invite/auth-access',
  '/app-redirect',
];

function Root() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { isLoading, mutate: callVerifyUserToken } = useMutation({
    onError: () => onLogoutUser(true),
    mutationFn: verifyUserToken,
    onSuccess: onVerifyUserSuccess,
  });

  useEffect(() => {
    // Persist the redirect host from the entry URL up-front. The already-
    // authenticated path goes straight to business/app selection (which reads
    // the redirect host from storage), skipping the login screen where it is
    // normally captured. Without this, a caller passing ?host=... (e.g. an MCP
    // client using host=mcp-local) would fall back to the default app redirect.
    const redirectHost = searchParams.get('host');
    if (redirectHost) {
      LocalStorageHelper.setRedirectHost(redirectHost);
    } else {
      LocalStorageHelper.resetRedirectHost();
    }

    // Capture the target app from the entry URL (e.g. an MCP client passing
    // ?app=atlas). Like the host above, the already-authenticated path skips the
    // login screen where this is normally captured — without it, a user with
    // both Prime and Atlas is shown the app picker instead of going straight to
    // the requested app.
    const loginApp = searchParams.get('app');
    if (loginApp) {
      LocalStorageHelper.setAppLogin(loginApp);
    }

    if (
      location?.pathname?.includes('/s/') ||
      EXCLUDE_ROUTES.includes(location?.pathname)
    ) {
      setIsAuthenticated(true);
    } else if (LocalStorageHelper.getAccessToken()) {
      callVerifyUserToken();
    } else {
      onLogoutUser(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onVerifyUserSuccess(resp) {
    setIsAuthenticated(true);

    const redirect = getRedirectRoute(resp);

    if (redirect) {
      navigate(redirect);
      //Vent.onUserIdentified();
    }
  }

  function onLogoutUser(trackLogout = false) {
    if (trackLogout) {
      AnalyticsService.onUleLogout();
    }
    LocalStorageHelper.clearUserDataOnLogout();
    setIsAuthenticated(false);
    // Preserve the entry query (e.g. ?app=&host=) so the login screen can
    // re-capture the redirect host after the logout/clear above.
    navigate(`/login/email-mobile${location?.search || ''}`);
    Vent.onUserUnidentified();
  }

  return (
    <div className="bg-up-clr-lightest-grey w-full min-h-screen flex flex-col">
      <TopBar />
      <div className="flex items-center justify-center w-full flex-grow pt-8 pb-10 px-4">
        {isLoading ? (
          <LoadingCard text="Loading your businesses..." />
        ) : isAuthenticated ? (
          <Outlet />
        ) : null}
      </div>
    </div>
  );
}

export default Root;
